// Página "DataBunker Insights". Muestra los reportes HTML que el equipo interno
// publicó y asignó al usuario desde el portal (Reports › HTML), seleccionables desde
// un carrusel de tabs arriba, y renderiza el activo en un <iframe SANDBOX> a todo el ancho.
//
// CLAVE DE SEGURIDAD (ver iframe abajo): el HTML del reporte NO se inyecta en el
// DOM de la app (nada de dangerouslySetInnerHTML). Se renderiza dentro de un
// iframe con sandbox SIN allow-same-origin, así el reporte corre en un origen
// "nulo": puede tener JS/gráficas, pero NO puede leer el token de sesión, el
// localStorage ni llamar a la API como el usuario.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { listInsightsReports, getInsightsReport } from '../../services/insightsService';
import './InsightsPage.css';

// Mismo sandbox que la vista previa del portal (Reports › HTML), para que el equipo vea
// exactamente lo que verá el cliente:
//   allow-scripts                     gráficas y JS del reporte
//   allow-popups (+ escape-sandbox)   las ligas externas abren normal en otra pestaña
//   allow-modals                      alert / confirm / botón de imprimir del reporte
//   allow-downloads                   exportar CSV/imagen desde el reporte
// NUNCA allow-same-origin: le devolvería acceso a la sesión del usuario.
const SANDBOX = 'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads';

const fecha = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

const mensajeError = (e, fallback) => (e?.status === 401 ? 'Tu sesión expiró. Vuelve a iniciar sesión.' : fallback);

export default function InsightsPage() {
  const { user } = useAuthenticator((context) => [context.user]);
  const username = user?.username;

  const [reports, setReports] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [current, setCurrent] = useState(null);   // { id, title, html_content, ... }
  const [docLoading, setDocLoading] = useState(false);
  const [error, setError] = useState(null);
  const pedido = useRef(null);   // id del último reporte pedido (descarta respuestas viejas)

  // 1) Lista de reportes del usuario (metadata).
  useEffect(() => {
    if (!username) return;
    let alive = true;
    setListLoading(true);
    listInsightsReports(username)
      .then((res) => {
        if (!alive) return;
        const list = res.reports || [];
        setReports(list);
        if (list.length) setSelectedId((prev) => (list.some((r) => r.id === prev) ? prev : list[0].id));
      })
      .catch((e) => { if (alive) setError(mensajeError(e, 'No se pudieron cargar los reportes')); })
      .finally(() => { if (alive) setListLoading(false); });
    return () => { alive = false; };
  }, [username]);

  // 2) Documento seleccionado (trae el html_content).
  const loadReport = useCallback(async (id) => {
    if (!username || !id) return;
    pedido.current = id;
    try {
      setDocLoading(true);
      setError(null);
      const res = await getInsightsReport(id, username);
      if (pedido.current === id) setCurrent(res.report || null);
    } catch (e) {
      if (pedido.current === id) {
        setError(mensajeError(e, 'No se pudo abrir el reporte'));
        setCurrent(null);
      }
    } finally {
      if (pedido.current === id) setDocLoading(false);
    }
  }, [username]);

  useEffect(() => { if (selectedId) loadReport(selectedId); }, [selectedId, loadReport]);

  return (
    <div className="ins-page">
      {/* Barra compacta: solo el carrusel de reportes */}
      <div className="ins-bar">
        <div className="ins-tabs">
          {listLoading ? (
            <span className="ins-muted">Cargando reportes…</span>
          ) : reports.length === 0 ? (
            <span className="ins-muted">Aún no tienes reportes asignados.</span>
          ) : (
            reports.map((r) => (
              <button
                key={r.id}
                className={`ins-tab ${r.id === selectedId ? 'active' : ''}`}
                onClick={() => setSelectedId(r.id)}
                title={[r.description || r.title, r.published_at && `Actualizado el ${fecha(r.published_at)}`].filter(Boolean).join(' · ')}
              >
                {r.title}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Visor a todo el ancho */}
      <section className="ins-viewer">
        {error && <div className="ins-error">{error}</div>}
        {docLoading ? (
          <div className="ins-muted ins-center">Abriendo reporte…</div>
        ) : current ? (
          // key = id + versión: recrea el iframe al cambiar de reporte o si se publicó otra versión.
          <iframe
            key={`${current.id}-${current.version || 0}`}
            className="ins-frame"
            title={current.title}
            sandbox={SANDBOX}
            srcDoc={current.html_content}
          />
        ) : (!listLoading && reports.length > 0) ? (
          <div className="ins-muted ins-center">Selecciona un reporte para verlo.</div>
        ) : null}
      </section>
    </div>
  );
}
